const BRAND_DISPLAY_NAMES: Record<string, string> = {
  lastminute: 'lastminute.com',
  volagratis: 'volagratis',
  rumbo: 'rumbo',
};

function normalizeBrand(brandName?: string | null): string {
  if (!brandName) return 'lastminute';
  return brandName.toLowerCase().trim();
}

export function getBrandDisplayName(brandName?: string | null): string {
  const normalized = normalizeBrand(brandName);
  return BRAND_DISPLAY_NAMES[normalized] ?? BRAND_DISPLAY_NAMES.lastminute;
}

export function replaceBrandPlaceholder(
  text: string,
  brandName?: string | null,
): string {
  const displayName = getBrandDisplayName(brandName);
  return text.replace(/__brandName__/g, displayName);
}

export function replaceTermsLinkPlaceholder(
  text: string,
  termsLink: string,
): string {
  return text.replace(/__termsLink__/g, termsLink);
}

export function replacePrivacyLinkPlaceholder(
  text: string,
  privacyLink: string,
): string {
  return text.replace(/__privacyLink__/g, privacyLink);
}


export interface ParsedSegment {
  type: 'text' | 'link';
  content: string;
  url?: string | null;
}

/**
 * Parses a template string containing __termsLinkN__TEXT__termsLinkN__,
 * __privacyLink__TEXT__privacyLink__, and TEXT
 * placeholders into an array of segments suitable for rendering as JSX
 * (text nodes and optional <a> links).
 *
 * @param template - The string containing placeholder pairs
 * @param urlMap - Maps placeholder names (e.g. "termsLink", "termsLink1", "privacyLink", "marketingLink") to URLs.
 *                 A null or undefined value means the link is not configured → render as plain text.
 *
 * @example
 * parseLinkTemplate(
 *   "I accept the __termsLink__T&C__termsLink__",
 *   { termsLink: "https://example.com" }
 * )
 * // [{ type: 'text', content: 'I accept the ' }, { type: 'link', content: 'T&C', url: 'https://example.com' }]
 */
function getFallbackLabel(key: string): string | undefined {
  if (key === 'marketingLink') return 'Marketing Link';
  if (key === 'termsLink' || /^termsLink\d+$/.test(key)) return 'Terms & Condition';
  return undefined;
}

export function parseLinkTemplate(
  template: string,
  urlMap: Record<string, string | null | undefined>,
): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  const regex = /__(termsLink\d*|privacyLink|marketingLink)__(.*?)__\1__/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    const key = match[1];
    const content = match[2];
    const matchStart = match.index;
    const matchEnd = regex.lastIndex;

    if (matchStart > lastIndex) {
      segments.push({
        type: 'text',
        content: template.slice(lastIndex, matchStart),
      });
    }

    const url = urlMap[key] || null;
    const displayContent = url ? content : (getFallbackLabel(key) ?? content);

    segments.push({
      type: 'link',
      content: displayContent,
      url,
    });

    lastIndex = matchEnd;
  }

  if (lastIndex < template.length) {
    segments.push({
      type: 'text',
      content: template.slice(lastIndex),
    });
  }

  return segments;
}
